/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  type CurrencyAmount,
  DateFormats,
  formatISODate,
  formatNumber,
} from '@omnipresentgroup/design-system';
import type { InvoiceReportEmployeeBreakdownItem } from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

export enum InvoiceReportEmployeeBreakdownColumnHeaders {
  billingItems = 'Billing items',
  amount = 'Total billing',
  currentInvoice = 'This invoice',
  difference = 'Difference',
}

export const useInvoiceReportEmployeeBreakdownTableConfig = ({
  totalAmount,
  comparedTotalAmount,
  comparedPayPeriod,
}: {
  totalAmount?: CurrencyAmount;
  comparedTotalAmount?: CurrencyAmount;
  comparedPayPeriod?: string;
}) => {
  const columnHelper = useMemo(
    () => createColumnHelper<InvoiceReportEmployeeBreakdownItem>(),
    [],
  );

  const columns: ColumnDef<InvoiceReportEmployeeBreakdownItem, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: 'billing-items',
        cell: (info) => info.row.original.label ?? '',
        header: () => InvoiceReportEmployeeBreakdownColumnHeaders.billingItems,
        footer: () => 'Total',
      }),
      ...(comparedTotalAmount && comparedPayPeriod
        ? [
            columnHelper.display({
              id: 'compared-amount',
              cell: (info) =>
                info.row.original.compared?.amount
                  ? formatNumber({
                      input: info.row.original.compared.amount,
                      currency: comparedTotalAmount.currency,
                    })
                  : '-',
              header: () =>
                `${formatISODate(
                  comparedPayPeriod,
                  DateFormats.DateWithoutDay,
                )} (${comparedTotalAmount.currency})`,
              footer: () =>
                comparedTotalAmount
                  ? formatNumber({
                      input: Number(comparedTotalAmount.amount),
                      currency: comparedTotalAmount.currency,
                    })
                  : '-',
              meta: {
                style: {
                  textAlign: 'right',
                },
              },
            }),
          ]
        : []),
      columnHelper.display({
        id: 'amount',
        cell: (info) =>
          formatNumber({
            input: info.row.original.amount,
            currency: totalAmount?.currency,
          }),
        header: () =>
          `${
            comparedTotalAmount
              ? InvoiceReportEmployeeBreakdownColumnHeaders.currentInvoice
              : InvoiceReportEmployeeBreakdownColumnHeaders.amount
          } ${totalAmount?.currency ? `(${totalAmount?.currency})` : ''}`,
        footer: () =>
          totalAmount
            ? formatNumber({
                input: Number(totalAmount.amount),
                currency: totalAmount?.currency,
              })
            : '-',
        meta: {
          style: {
            textAlign: 'right',
          },
        },
      }),
      ...(comparedTotalAmount
        ? [
            columnHelper.display({
              id: 'compared-amount-difference',
              cell: (info) =>
                info.row.original.compared?.amount
                  ? formatNumber({
                      input:
                        info.row.original.amount -
                        info.row.original.compared.amount,
                      currency: totalAmount?.currency,
                    })
                  : '-',
              header: () =>
                InvoiceReportEmployeeBreakdownColumnHeaders.difference,
              footer: () => {
                const difference =
                  totalAmount && comparedTotalAmount
                    ? Number(totalAmount.amount) -
                      Number(comparedTotalAmount.amount)
                    : undefined;

                return difference && totalAmount
                  ? formatNumber({
                      input: difference,
                      currency: totalAmount.currency,
                    })
                  : '-';
              },
              meta: {
                style: {
                  textAlign: 'right',
                },
              },
            }),
          ]
        : []),
    ],
    [
      columnHelper,
      comparedPayPeriod,
      comparedTotalAmount,
      totalAmount?.amount,
      totalAmount?.currency,
    ],
  );

  return {
    columns,
    columnHelper,
  };
};
